<template>
    <div class="add-order">
        <h3 v-if="showTitle">{{ t('dashboard-addOrder-title') }}</h3>
        <div class="field" v-show="showDate">
            <label>{{ t('dashboard-addOrder-date_time') }}</label>
            <datetime-with-clear
                type="datetime"
                :zone="placeTimezone"
                :value-zone="placeTimezone"
                v-model="date"
            ></datetime-with-clear>
        </div>
        <div class="field">
            <label>{{ t('dashboard-addOrder-order_value') }}</label>
            <input
                type="tel"
                ref="valueField"
                @keydown.enter="
                    formType === 'add' ? $refs.phoneField.focus() : saveOrder()
                "
                class="form-control"
                :placeholder="t('dashboard-addOrder-order_value')"
                v-money="money"
                v-model="value"
            />
        </div>
        <div v-if="formType === 'add' && !phoneReadOnly" class="field">
            <label>{{ t('dashboard-addOrder-client_phone') }}</label>
            <kv-phone-input
                :disabled="formType === 'update' || phoneReadOnly"
                :default-country="placeCountry"
                placeholder="(##) #####-####"
                v-model="phone"
                ref="phoneField"
            />
        </div>
        <template>
            <div
                v-for="(field, index) in fields"
                class="field"
                :key="'field-' + field.id"
            >
                <label>{{ field.field_name }}</label>
                <template v-if="field.type === 'text' || field.type === 'coupon'">
                    <simple-autocomplete
                        v-if="field.enable_autocomplete === '1'"
                        :options="hasFixedOptions(field) ? getOptions(field) : []"
                        :search-fn="hasFixedOptions(field) ? null : (input) => searchFieldValues(field, input)"
                        :value="{
                            value: fieldValues[field.id],
                            label: fieldValues[field.id],
                        }"
                        @input="($event) => (fieldValues[field.id] = $event)"
                    />
                    <input
                        v-else
                        type="text"
                        class="form-control"
                        v-model="fieldValues[field.id]"
                    />
                </template>
                <template v-if="field.type === 'user'">
                    <select
                        v-model="fieldValues[field.id]"
                        class="form-control"
                    >
                        <option
                            v-for="(user, index) in getOptions(field)"
                            :value="user.value"
                            :key="index"
                        >
                            {{ user.label }}
                        </option>
                    </select>
                </template>
                <template v-if="field.type === 'textarea'">
                    <textarea
                        type="text"
                        class="form-control"
                        v-model="fieldValues[field.id]"
                    ></textarea>
                </template>
                <template v-if="field.type === 'checkbox'">
                    <div class="options">
                        <div
                        class="field"
                        v-for="(option, index) in getActiveOptions(field)"
                        :key="index"
                        >
                        <label :for="`checkbox-${field.id}-${index}`">
                            <input
                                type="checkbox"
                                :value="option"
                                :id="`checkbox-${field.id}-${index}`"
                                v-model="fieldValues[field.id]"
                            />
                            {{ option.text }}
                        </label>
                        </div>
                    </div>
                </template>
                <template v-if="field.type === 'date'">
                    <datetime-with-clear
                        type="datetime"
                        value-zone="America/Sao_Paulo"
                         :id="`date-${field.id}-${index}`"
                       v-model="fieldValues[field.id]"
                    ></datetime-with-clear>
                </template>
                <template v-if="field.type === 'number'">
                    <input
                        type="number"
                        class="form-control"
                        v-model="fieldValues[field.id]"
                    />
                </template>
            </div>
        </template>
        <div class="message message-success" v-if="success">
            {{ t('dashboard-addOrder-success_message') }}
            <br />
            <div
                class="message message-error"
                v-if="
                    lastClient && success && parseInt(lastClient.orders) === 1
                "
            >
                {{ t('dashboard-addOrder-new_client_message') }}
            </div>
            <div
                class="message message-error"
                v-if="
                    lastClient &&
                    success &&
                    parseInt(lastClient.orders) > 1 &&
                    !lastClient.email
                "
            >
                {{ t('dashboard-addOrder-not_registered_message') }}
            </div>
        </div>
        <span class="message message-error" v-if="valid === false">
            {{ error }}
        </span>
        <confirm
            id="confirm-anonymous"
            ref="confirm"
            @confirm="saveAnonymousOrder"
            @cancel="reset"
        >
            <template slot="title">{{ t('dashboard-addOrder-anonymous_title') }}</template>
            <template slot="body">
                <div v-html="t('dashboard-addOrder-anonymous_body')"></div>
            </template>
        </confirm>
        <confirm
            id="confirm-value"
            ref="confirmValue"
            @confirm="confirmValue"
            @cancel="reset"
        >
            <template slot="title"
                >{{ t('dashboard-addOrder-suspect_value_title', { 0: formattedValue }) }}</template
            >
            <template slot="body">
                <div v-html="t('dashboard-addOrder-suspect_value_body', { 0: formattedValue })"></div>
            </template>
        </confirm>
        <div class="buttons">
            <button
                ref="saveOrder"
                class="btn btn-primary"
                @click="saveOrder"
                :disabled="loading"
            >
                <span class="fa fa-check" v-if="!loading"></span>
                <span class="fa fa-spin fa-spinner" v-if="loading"></span>
                {{ formType === "add" ? t('dashboard-addOrder-register_button') : t('dashboard-addOrder-save_button') }}
            </button>
        </div>
    </div>
</template>

<script>
import keviApi from "../../keviApi";
import { loadPdvOrderFields, PDV_ORDER_FIELDS_URL } from "../../helpers/pdvOrderFields";
import Confirm from "./../../components/confirm/confirm";
import DatetimeWithClear from "../../components/datetime-with-clear/datetime-with-clear.vue";
import SimpleAutocomplete from "../../components/simple-autocomplete/simple-autocomplete";
import { getCurrencySymbol } from "../../helpers/currency";
import phoneMixin from "../../mixins/phone-mixin";
import KvPhoneInput from "../../components/kv-phone-input/kv-phone-input.vue";

export default {
    mixins: [phoneMixin],
    props: {
        formType: {
            type: String,
            default: "add",
        },
        toUpdateOrder: {
            type: Object,
            default: () => {
                return null;
            },
        },
        defaultPhone: {
            type: String,
            default: "",
        },
        showTitle: {
            type: Boolean,
            default: true,
        },
        showDate: {
            type: Boolean,
            default: true,
        },
    },
    components: {
        confirm: Confirm,
        "datetime-with-clear": DatetimeWithClear,
        SimpleAutocomplete,
        KvPhoneInput,
    },
    data: function () {
        const place = this.$store.state.place || {};
        const currency = place.currency || "BRL";
        const locale = place.locale || "pt-BR";
        const useEnSeparators = locale.indexOf("en") === 0;
        return {
            loading: false,
            id: null,
            phone: this.defaultPhone ? this.defaultPhone : null,
            value: null,
            valid: null,
            error: null,
            success: null,
            lastClient: null,
            anonymous: false,
            phoneReadOnly: !!this.defaultPhone,
            date: this.showDate ? null : new Date().toISOString(),
            money: {
                decimal: useEnSeparators ? "." : ",",
                thousands: useEnSeparators ? "," : ".",
                prefix: getCurrencySymbol(currency, locale) + " ",
                suffix: "",
                precision: 2,
                masked: false /* doesn't work with directive */,
            },
            fieldValues: {},
            warningOrderValue: 1000,
            valueConfirmed: false,
            fields: [],
        };
    },
    created: function () {
        let options = keviApi.getOptions(
            "place/" + this.$store.state.place.id + "/details",
            "GET",
        );
        this.$http(options).then((response) => {
            this.warningOrderValue = response.body["warning_order_value"];
        });
        this.setData();
        this.loadFields();
    },
    watch: {
        toUpdateOrder(val, old) {
            if (!old || val.id !== old.id) {
                this.setData();
                this.loadFields();
            }
        },
    },
    computed: {
        formattedValue() {
            return this.value ? this.value : 0;
        },
        place() {
            return this.$store.state.place;
        },
        placeTimezone() {
            return (
                (this.$store.state.place && this.$store.state.place.timezone) ||
                "America/Sao_Paulo"
            );
        },
        user() {
            return this.$store.state.user;
        }
    },
    methods: {
        getActiveOptions(field) {
            if (!field.options || !Array.isArray(field.options)) return [];
            return field.options.filter(
                (opt) => !opt.removed && opt.active
            );
        },
        getOptions(field) {
            if (field.type === "user") {
                return field.options;
            }
            return field.options.map((o) => ({ value: o, label: o }));
        },
        hasFixedOptions(field) {
            return Array.isArray(field.options) && field.options.length > 0;
        },
        searchFieldValues(field, input) {
            const options = keviApi.getOptions(
                `order-fields/${field.id}/values?search=${encodeURIComponent(input)}&limit=20`,
                "GET",
            );
            return this.$http(options).then(
                (response) => (response.body || []).map((v) => ({ value: v, label: v })),
                () => [],
            );
        },
        loadFields() {
            loadPdvOrderFields(this.$store.state.place?.id, () =>
                this.$http(keviApi.getOptions(PDV_ORDER_FIELDS_URL, "GET")).then(
                    (response) => response.body,
                ),
            ).then((fields) => {
                this.fields = fields;
                const savedEntries = {};
                if (this.formType === "update" && this.toUpdateOrder?.fields) {
                    Object.values(this.toUpdateOrder.fields).forEach((entry) => {
                        if (
                            entry.orders_fields_id &&
                            entry.value !== null &&
                            entry.value !== undefined
                        ) {
                            savedEntries[entry.orders_fields_id] = entry;
                            savedEntries[String(entry.orders_fields_id)] = entry;
                        }
                    });
                }

                const savedValues =
                    this.formType === "update" && this.toUpdateOrder?.field_values
                        ? this.toUpdateOrder.field_values
                        : {};

                this.fields.forEach((field) => {
                    const entry =
                        savedEntries[field.id] || savedEntries[String(field.id)];
                    const saved =
                        savedValues[field.id] ??
                        savedValues[String(field.id)] ??
                        savedValues[field.field_identifier] ??
                        entry?.value;

                    if (field.type === "checkbox") {
                        let parsedSaved = saved;
                        if (typeof parsedSaved === "string") {
                            try {
                                parsedSaved = JSON.parse(parsedSaved);
                            } catch (e) {
                                parsedSaved = [saved];
                            }
                        }
                        if (!Array.isArray(parsedSaved)) {
                            parsedSaved = parsedSaved ? [parsedSaved] : [];
                        }

                        const savedTexts = parsedSaved
                            .map((option) =>
                                typeof option === "string"
                                    ? option
                                    : option?.text || option?.label || option?.value
                            )
                            .filter(Boolean);

                        const matched = (field.options || []).filter((option) => {
                            const optionText =
                                typeof option === "string"
                                    ? option
                                    : option?.text || option?.label || option?.value;
                            return savedTexts.includes(optionText);
                        });
                        this.$set(this.fieldValues, field.id, matched);
                    } else if (saved !== undefined) {
                        this.$set(this.fieldValues, field.id, saved);
                    } else if (field.type === "checkbox") {
                        this.$set(this.fieldValues, field.id, []);
                    }
                });
            });
        },
        saveOrder() {
            if (this.isValid() && !this.loading) {
                this.loading = true;
                let data = this.getData();
                let options = keviApi.getOptions(
                    "orders",
                    this.formType === "add" ? "POST" : "PUT",
                    data,
                );
                this.$http(options).then(
                    (response) => {
                        this.lastClient = response?.body?.client;
                        this.$emit("orderSaved", response.body);
                        this.success = true;
                        let successMessage = this.t('dashboard-addOrder-generic_success');
                        if (this.lastClient && this.formType === "add") {
                            if (this.lastClient.first_name) {
                                successMessage = this.t('dashboard-addOrder-personalized_success', {
                                    0: this.lastClient.first_name,
                                    1: this.lastClient.balance
                                });
                            } else {
                                if (
                                    this.lastClient &&
                                    parseInt(this.lastClient.orders) === 1
                                ) {
                                    successMessage = this.t('dashboard-addOrder-welcome_points', {
                                        0: this.lastClient.balance
                                    });
                                } else {
                                    successMessage = this.t('dashboard-addOrder-thanks_points', {
                                        0: this.lastClient.balance
                                    });
                                }
                            }
                        }
                        this.$store.dispatch("notification/success", {
                            text: successMessage,
                            duration: 5000,
                        });
                        window.mixpanel?.track(
                            this.formType === "add"
                                ? "New Order"
                                : "Update Order",
                            {
                                id: this.formType === "add" ? null : this.id,
                                client_phone_number: data.phone,
                                client_id: this.lastClient
                                    ? this.lastClient.id
                                    : null,
                                client_balance: this.lastClient
                                    ? this.lastClient.balance
                                    : null,
                                order_value: data.value,
                                order_callback_message: successMessage,
                                place_id: this.place.id,
                                place_name: this.user.place_name,
                                place_branch_name: this.user.branch_title,
                                user_name: this.user.name,
                                user_username: this.user.username,
                                user_email: this.user.email,
                            },
                        );
                        this.reset();
                        this.loading = false;
                    },
                    () => {
                        this.loading = false;
                    },
                );
            }
        },
        saveAnonymousOrder() {
            this.anonymous = true;
            return this.saveOrder();
        },
        confirmValue() {
            this.valueConfirmed = true;
            return this.saveOrder();
        },
        reset() {
            if (this.anonymous === true) {
                this.lastClient = null;
            }
            this.date = this.showDate ? null : new Date().toISOString();
            this.phone = this.defaultPhone ? this.defaultPhone : null;
            this.error = null;
            this.value = null;
            this.anonymous = false;
            this.valueConfirmed = false;
            this.fieldValues = {};
            this.fields.forEach(field => {
                    if (field.type === 'checkbox') {
                        this.$set(this.fieldValues, field.id, []);
                    }
                });
            this.$refs.valueField.focus();
        },
        getData() {
            return {
                id: this.formType === "add" ? null : this.id,
                value: this.value
                    ? parseFloat(
                          this.value
                              .replace(".", "")
                              .replace(",", ".")
                              .replace(/[^0-9.]/gm, ""),
                      )
                    : 0,
                phone: this.phone,
                field_values: JSON.parse(JSON.stringify(this.fieldValues)),
                ...(this.showDate ? { date: this.date } : {}),
            };
        },
        setData() {
            if (this.formType === "update" && this.toUpdateOrder) {
                this.id = this.toUpdateOrder.id;
                this.value = this.toUpdateOrder.value;
                this.date = this.createdToInstantISO(this.toUpdateOrder.created);
                this.phone = this.defaultPhone ? this.defaultPhone : null;
            }
        },
        createdToInstantISO(created) {
            if (!created) {
                return null;
            }
            // The datetime picker (value-zone = place tz) needs an absolute
            // instant. Offset-bearing values (RFC2822 / ISO with Z or +/-hh:mm)
            // are already instants; naive "Y-m-d H:i:s" values are canonical
            // GMT-3 storage datetimes, so pin them to -03:00.
            const hasOffset = /[zZ]|[+-]\d{2}:?\d{2}/.test(created);
            const date = hasOffset
                ? new Date(created)
                : new Date(String(created).replace(" ", "T") + "-03:00");
            return isNaN(date.getTime()) ? null : date.toISOString();
        },
        isPhoneValid() {
            return this.isValidPhone(this.phone);
        },
        isValid() {
            this.valid = null;
            this.success = null;
            let data = this.getData();
            if (data.value <= 0 || !data.value) {
                this.valid = false;
                this.error = this.t('dashboard-addOrder-value_error');
                return false;
            }
            if (data.value > this.warningOrderValue && !this.valueConfirmed) {
                this.$refs.confirmValue.open();
                return false;
            }
            if (!this.isPhoneValid()) {
                if (!this.anonymous) {
                    this.$refs.confirm.open();
                    return false;
                } else {
                    return true;
                }
            }
            return true;
        },
        t(term, replaces) {
            return this.$root.t(term, replaces);
        },
    },
};
</script>
